// =============================================================================
// Lairo Status — pure data model.
// Status vocabulary, seed data, and derived helpers. No Firebase / React deps,
// so the Node seed script and the browser app can both import it.
// =============================================================================

export const DAY = 86400000;

// ---- status vocab ----------------------------------------------------------
export const COMPONENT_STATUS = {
  operational: { label: "Operational", tone: "op" },
  degraded: { label: "Degraded performance", tone: "deg" },
  partial: { label: "Partial outage", tone: "part" },
  major: { label: "Major outage", tone: "maj" },
  maintenance: { label: "Under maintenance", tone: "mnt" },
};
export const INCIDENT_STATUS = {
  investigating: { label: "Investigating", tone: "maj" },
  identified: { label: "Identified", tone: "part" },
  monitoring: { label: "Monitoring", tone: "deg" },
  resolved: { label: "Resolved", tone: "op" },
};
export const MAINT_STATUS = {
  scheduled: { label: "Scheduled", tone: "mnt" },
  in_progress: { label: "In progress", tone: "deg" },
  completed: { label: "Completed", tone: "op" },
};
export const IMPACT = {
  none: { label: "None", tone: "op" },
  minor: { label: "Minor", tone: "deg" },
  major: { label: "Major", tone: "part" },
  critical: { label: "Critical", tone: "maj" },
  maintenance: { label: "Maintenance", tone: "mnt" },
};

export function uid(p) {
  return p + "_" + Math.random().toString(36).slice(2, 9);
}

// ---- 90-day uptime history generator ---------------------------------------
// marks: { daysAgo: { status, pct } } — everything else is 100% operational.
export function genHistory(marks = {}, ref = Date.now()) {
  const d0 = new Date(ref);
  d0.setHours(0, 0, 0, 0);
  const today0 = d0.getTime();
  const days = [];
  for (let i = 89; i >= 0; i--) {
    const date = today0 - i * DAY;
    const m = marks[i];
    days.push({ date, status: m ? m.status : "operational", pct: m ? m.pct : 100 });
  }
  return days;
}

// ---- seed state ------------------------------------------------------------
export function seedState(now = Date.now()) {
  const components = [
    { id: "api", name: "API", group: null, description: "Core REST & GraphQL endpoints", status: "operational", history: genHistory({}, now) },
    { id: "app", name: "Web App", group: null, description: "app.lairo.ai dashboard", status: "operational", history: genHistory({}, now) },
    { id: "procore", name: "Procore", group: "Integrations", description: "Project & document sync", status: "operational", history: genHistory({}, now) },
    { id: "autodesk", name: "Autodesk", group: "Integrations", description: "ACC / BIM 360 sync", status: "operational", history: genHistory({}, now) },
    { id: "cmic", name: "CMiC", group: "Integrations", description: "Financials & ERP sync", status: "operational", history: genHistory({}, now) },
    { id: "box", name: "Box", group: "Integrations", description: "File storage sync", status: "operational", history: genHistory({}, now) },
    { id: "gdrive", name: "Google Drive", group: "Integrations", description: "File storage sync", status: "operational", history: genHistory({}, now) },
    { id: "teams", name: "Microsoft Teams", group: "Integrations", description: "Notifications & channel sync", status: "operational", history: genHistory({}, now) },
  ];

  const incidents = [];
  const maintenances = [];

  return {
    page: { name: "Lairo", tagline: "Status" },
    announcement: { active: false, tone: "info", text: "" },
    components,
    incidents,
    maintenances,
    updatedAt: now,
  };
}

// ---- derived helpers -------------------------------------------------------
const STATUS_ORDER = ["major", "partial", "degraded", "maintenance", "operational"];

export function overall(s) {
  if (!s) return "operational";
  const active = (s.incidents || []).filter((i) => i.status !== "resolved");
  const liveMaint = (s.maintenances || []).filter((m) => m.status === "in_progress");
  let worst = "operational";
  (s.components || []).forEach((c) => {
    if (STATUS_ORDER.indexOf(c.status) < STATUS_ORDER.indexOf(worst)) worst = c.status;
  });
  active.forEach((i) => {
    const map = { critical: "major", major: "partial", minor: "degraded", maintenance: "maintenance" };
    const eq = map[i.impact] || "degraded";
    if (STATUS_ORDER.indexOf(eq) < STATUS_ORDER.indexOf(worst)) worst = eq;
  });
  if (worst === "operational" && liveMaint.length) worst = "maintenance";
  return worst;
}

export function uptimePct(component) {
  if (!component.history || !component.history.length) return 100;
  const sum = component.history.reduce((a, d) => a + d.pct, 0);
  return sum / component.history.length;
}

// ---- overall-status presentation (shared by public hero + admin dashboard) -
export const OVERALL = {
  operational: { title: "All systems operational", tone: "op", icon: "check" },
  degraded: { title: "Degraded performance", tone: "deg", icon: "warn" },
  partial: { title: "Partial system outage", tone: "part", icon: "alert" },
  major: { title: "Major system outage", tone: "maj", icon: "x" },
  maintenance: { title: "Under maintenance", tone: "mnt", icon: "wrench" },
};
